/**
 * Group Chat real-time – Ably.
 * Subscribes to group:${groupId} and user:${userId}.
 * See docs/ABLY_CUSTOMER_PORTAL.md.
 */
import { getAblyRealtime, subscribeToChannel } from './ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { apiService } from './api';

const GROUP_CHANNEL_EVENTS = ['group:message:new', 'group:comment:new'] as const;
const USER_GROUP_EVENTS = [
  'group:message:notification',
  'group:comment:new',
  'group:comment:sentiment:updated',
] as const;

export class GroupChatWebSocketService {
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private unsubGroup: (() => void) | null = null;
  private unsubUser: (() => void) | null = null;
  private currentGroupId: string | null = null;
  private userId: string | null = null;

  connect(): void {
    this.disconnect();

    const { accessToken } = apiService.getTokens();
    if (!accessToken) {
      console.warn('[GroupChatWebSocket] No access token');
      return;
    }

    const uid = getUserIdFromToken(accessToken);
    if (!uid) {
      console.warn('[GroupChatWebSocket] Could not extract userId');
      return;
    }

    this.userId = uid;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[GroupChatWebSocket] handler error', e);
        }
      });
    };

    this.unsubUser = subscribeToChannel(
      `user:${uid}`,
      USER_GROUP_EVENTS as unknown as readonly string[],
      emit
    );

    getAblyRealtime().connection.once('connected', () => {
      emit('connect', { socketId: 'ably' });
    });
  }

  joinGroupRoom(groupId: string): void {
    if (this.currentGroupId === groupId && this.unsubGroup) return;
    if (this.unsubGroup) {
      this.unsubGroup();
      this.unsubGroup = null;
    }

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[GroupChatWebSocket] handler error', e);
        }
      });
    };

    this.currentGroupId = groupId;
    this.unsubGroup = subscribeToChannel(
      `group:${groupId}`,
      GROUP_CHANNEL_EVENTS as unknown as readonly string[],
      emit
    );
  }

  leaveGroupRoom(groupId: string): void {
    if (this.currentGroupId === groupId && this.unsubGroup) {
      this.unsubGroup();
      this.unsubGroup = null;
      this.currentGroupId = null;
    }
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    return this.unsubUser != null;
  }

  disconnect(): void {
    if (this.unsubGroup) {
      this.unsubGroup();
      this.unsubGroup = null;
    }
    if (this.unsubUser) {
      this.unsubUser();
      this.unsubUser = null;
    }
    this.currentGroupId = null;
    this.userId = null;
    this.eventHandlers.clear();
  }
}

export const groupChatWebSocketService = new GroupChatWebSocketService();
